import React, { useEffect, useState } from 'react';
import { FilterState, TimePreset } from './types';

type Props = {
  value: FilterState;
  onChange: (next: FilterState) => void;
  colors: any;

  showUser?: boolean;           // default: false
  showActiveSummary?: boolean;  // default: false

  /** Pass up to two logos (React nodes). We'll render them separately from the boxes. */
  leftExtras?: React.ReactNode;
};

type Option = { value: string; label: string };

// ───────── App font (force BTitrBold) ─────────
const APP_FONT =
  `"BTitr","B Titr","BTitrBold","IRANSans","Inter",system-ui,"Segoe UI",Roboto,sans-serif`;

// 🚫 Tell password managers to ignore this section
const PM_IGNORE_ATTRS = {
  'data-1p-ignore': 'true',    // 1Password
  'data-lpignore': 'true',     // LastPass
  'data-bw-ignore': 'true',    // Bitwarden
  'data-dashlane': 'disabled', // Dashlane
  autoComplete: 'off' as const,
};

// Tiny MultiSelect
const MultiSelect: React.FC<{
  options: Option[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  colors: any;
}> = ({ options, value, onChange, placeholder, colors }) => {
  const toggle = (val: string) =>
    onChange(value.includes(val) ? value.filter(v => v !== val) : [...value, val]);

  const box: React.CSSProperties = {
    background: '#0b1220',
    border: `1px solid ${colors.border}`,
    color: colors.text,
    borderRadius: 8,
    padding: 6,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };
  const chip = (active: boolean): React.CSSProperties => ({
    border: `1px solid ${active ? colors.accent : colors.border}`,
    background: active ? 'rgba(96,165,250,0.12)' : 'transparent',
    color: active ? colors.text : colors.sub,
    borderRadius: 6,
    padding: '2px 6px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.6,
    fontFamily: APP_FONT,
  });

  return (
    <div style={{ display: 'grid', gap: 4, fontFamily: APP_FONT, fontWeight: 700 }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'nowrap',          // واحدها همیشه تو یک ردیف
          justifyContent: 'flex-start',
          ...box,
        }}
      >
        {options.length === 0 ? (
          <span style={{ color: colors.sub, fontSize: 11, fontFamily: APP_FONT, fontWeight: 700 }}>
            {placeholder || '—'}
          </span>
        ) : (
          options.map(opt => (
            <span
              key={opt.value}
              onClick={() => toggle(opt.value)}
              style={chip(value.includes(opt.value))}
              title={opt.label}
            >
              {opt.label}
            </span>
          ))
        )}
      </div>
      {value.length > 0 && (
        <div style={{ fontSize: 10, color: colors.sub, fontFamily: APP_FONT, fontWeight: 700 }}>
          انتخاب شده: {value.length} مورد
        </div>
      )}
    </div>
  );
};

const GlobalFilters: React.FC<Props> = ({
  value,
  onChange,
  colors,
  showUser = false,
  showActiveSummary = false,
  leftExtras,
}) => {
  const [unitOpts, setUnitOpts] = useState<Option[]>([]);
  const [opTypeOpts, setOpTypeOpts] = useState<Option[]>([]);
  const [userOpts, setUserOpts] = useState<Option[]>([]); // reserved

  // extract up to two logos
  const extras = React.Children.toArray(leftExtras as React.ReactNode);
  const extraA = extras[0] || null;
  const extraB = extras[1] || null;

  useEffect(() => {
    const load = async () => {
      const safeFetch = async (url: string) => {
        try {
          const r = await fetch(url, { credentials: 'include' });
          if (!r.ok) throw new Error('bad');
          return await r.json();
        } catch {
          return null;
        }
      };

      const IDEAL_UNITS = ['واحد PP', 'واحد الفین', 'واحد HDPE', 'واحد EO/EG', 'واحد UT'];
      const BY_ID: Record<string, string> = {
        '1': 'واحد PP',
        '2': 'واحد الفین',
        '3': 'واحد HDPE',
        '4': 'واحد EO/EG',
        '5': 'واحد UT',
      };
      const LEGACY_CODE: Record<string, string> = {
        'PU-100': 'واحد PP',
        'PU-200': 'واحد PP',
        'PU-300': 'واحد HDPE',
      };

      const normalizeUnits = (arr: any[]): Option[] => {
        const mapped = arr
          .map((u: any) => {
            const rawName = String(u?.name ?? u?.label ?? u ?? '').trim();
            const rawId   = String(u?.id ?? u?.value ?? '').trim();

            const label =
              (IDEAL_UNITS.includes(rawName) && rawName) ||
              LEGACY_CODE[rawName] ||
              BY_ID[rawId] ||
              rawName;

            if (!IDEAL_UNITS.includes(label)) return null;
            return { value: label, label };
          })
          .filter(Boolean) as Option[];

        return mapped.length ? mapped : IDEAL_UNITS.map(n => ({ value: n, label: n }));
      };

      const units = await safeFetch('/api/filters/units');
      setUnitOpts(
        Array.isArray(units) && units.length
          ? normalizeUnits(units)
          : IDEAL_UNITS.map(n => ({ value: n, label: n }))
      );

      const ops = await safeFetch('/api/filters/operation-types');
      setOpTypeOpts(
        Array.isArray(ops) && ops.length
          ? ops.map((o: any) => ({ value: String(o.value ?? o), label: String(o.label ?? o) }))
          : [
              { value: 'install', label: 'نصب' },
              { value: 'pickup',  label: 'برداشت' },
            ]
      );

      const users = await safeFetch('/api/filters/users');
      setUserOpts(
        Array.isArray(users) && users.length
          ? users.map((u: any) => ({
              value: String(u.username ?? u.id ?? u),
              label: String(u.fullname ?? u.name ?? u.username ?? u),
            }))
          : [
              { value: 'all',   label: 'همه' },
              { value: 'user1', label: 'کاربر ۱' },
              { value: 'user2', label: 'کاربر ۲' },
            ]
      );
    };
    load();
  }, []);

  // time helpers
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
  const rangeFromPreset = (p: TimePreset) => {
    const now = new Date();
    switch (p) {
      case 'today': { const d = todayISO(); return { start: d, end: d }; }
      case 'yesterday': { const y = addDays(now, -1).toISOString().slice(0, 10); return { start: y, end: y }; }
      case 'last7':   return { start: addDays(now, -6).toISOString().slice(0, 10), end: todayISO() };
      case 'last30':  return { start: addDays(now, -29).toISOString().slice(0, 10), end: todayISO() };
      case 'last90':  return { start: addDays(now, -89).toISOString().slice(0, 10), end: todayISO() };
      case 'last365': return { start: addDays(now, -364).toISOString().slice(0, 10), end: todayISO() };
      case 'custom':  return { start: value.start, end: value.end };
    }
  };

  const applyPreset = (p: TimePreset) => {
    const { start, end } = rangeFromPreset(p);
    onChange({ ...value, preset: p, start, end });
  };

  // styles
  const header: React.CSSProperties = {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: '8px 12px 0 12px',
    fontFamily: APP_FONT,
    fontWeight: 700,
  };
  const btn = (active?: boolean): React.CSSProperties => ({
    padding: '4px 8px',
    borderRadius: 8,
    border: `1px solid ${active ? colors.accent : colors.border}`,
    background: active ? 'rgba(96,165,250,0.12)' : 'transparent',
    color: active ? colors.text : colors.sub,
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
    lineHeight: 1.6,
    fontFamily: APP_FONT,
  });

  const box: React.CSSProperties = {
    background: '#0b1220',
    border: `1px solid ${colors.border}`,
    color: colors.text,
    borderRadius: 8,
    padding: '2px 4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 0,
    minHeight: 44,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  const labelS: React.CSSProperties = {
    fontSize: 10.5,
    color: colors.sub,
    marginBottom: 2,
    fontFamily: APP_FONT,
    fontWeight: 700,
  };

  return (
    <div dir="rtl" style={{ fontFamily: APP_FONT, fontWeight: 700 }} {...PM_IGNORE_ATTRS}>
      {/* row 1: presets */}
      <div style={header}>
        {[
          { key: 'today', label: 'امروز' },
          { key: 'yesterday', label: 'دیروز' },
          { key: 'last7', label: 'هفته گذشته' },
          { key: 'last30', label: 'ماه گذشته' },
          { key: 'last90', label: 'سه ماه گذشته' },
          { key: 'last365', label: 'سال گذشته' },
          { key: 'custom', label: 'دلخواه با تقویم' },
        ].map(p => (
          <button
            key={p.key}
            type="button"
            style={btn(value.preset === (p.key as TimePreset))}
            onClick={() => applyPreset(p.key as TimePreset)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* row 2: filters grid (left) | logos (right) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 420px', // فیلترها چپ، لوگوها راست
          alignItems: 'center',
          gap: 12,
          margin: '8px 12px',
          fontFamily: APP_FONT,
          fontWeight: 700,
        }}
      >
        {/* Filters grid (left) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            direction: 'ltr',          // ✅ این خط اضافه شد تا فلکس از RTL جدا بشه
            fontFamily: APP_FONT,
            fontWeight: 700,
          }}
        >
          {/* واحد */}
          <div
            style={{
              ...box,
              whiteSpace: 'nowrap',
              width: 'fit-content',
              paddingRight: 10,
              paddingLeft: 10,
              direction: 'rtl',       // متن و چیپ‌ها دوباره RTL
            }}
          >
            <div style={labelS}>واحد</div>
            <MultiSelect
              options={unitOpts}
              value={value.units}
              onChange={(units) => onChange({ ...value, units })}
              colors={colors}
              placeholder="(در حال بارگذاری یا گزینه‌ای موجود نیست)"
            />
          </div>
        </div>

        {/* Logos column (right) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            gap: 16,
            overflow: 'visible',
            zIndex: 1,
            fontFamily: APP_FONT,
            fontWeight: 700,
          }}
        >
          {/* logo A */}
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              transform: 'scale(2)',
              transformOrigin: 'center',
              willChange: 'transform',
              pointerEvents: 'none',
              fontFamily: APP_FONT,
              fontWeight: 700,
            }}
          >
            {extraA || null}
          </div>

          {/* logo B — Idea Tahavol Marun */}
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              transform: 'scale(1.8)',
              transformOrigin: 'center',
              willChange: 'transform',
              pointerEvents: 'none',
              position: 'relative',
              top: '-10px',
              fontFamily: APP_FONT,
              fontWeight: 700,
            }}
          >
            {extraB || null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalFilters;
